import * as React from 'react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StackedCellProps {
  /** Additional classes applied to the outer flex-col wrapper. */
  className?: string;
  /** Primary content — rendered as-is without default typography. */
  primary: React.ReactNode;
  /** Secondary content — rendered with `text-xs text-muted-foreground` by default. */
  secondary?: React.ReactNode;
  /** Additional classes merged into the secondary wrapper (via cn/twMerge). */
  secondaryClassName?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Two-line stacked data-table cell.
 *
 * Replaces the repeated `flex flex-col gap-0.5` pattern used in five
 * order-related cells: order-number, weight-volume, date-slot, carrier-driver,
 * and order-status-badge.
 *
 * The secondary line receives `text-xs text-muted-foreground` by default.
 * Override via `secondaryClassName` — conflicting Tailwind utilities (font-size,
 * text colour) are resolved by tailwind-merge, so passing e.g.
 * `secondaryClassName="text-[11px] text-destructive/80 line-clamp-2"` produces
 * the expected result.
 */
export function StackedCell({
  className,
  primary,
  secondary,
  secondaryClassName,
}: StackedCellProps): React.JSX.Element {
  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <div>{primary}</div>
      {secondary !== undefined ? (
        <div className={cn('text-xs text-muted-foreground', secondaryClassName)}>{secondary}</div>
      ) : null}
    </div>
  );
}
